// R-216 Task C2 — dense per-(step, jobType) lane row.
//
// Columns: Job | claim | ready | busy | tot | batches | docs | lock.
//   - claim  → E-051: the ONE explicit claim affordance per lane ("Claim
//              next"). Every step is a queue; every feedable lane gets the
//              same honestly-labeled control. Disabled (with a reason) when
//              nothing is ready or the lane is blocked; absent on
//              non-feedable lanes (export, terminal).
//   - name   → E-051: honest navigation to the lane's batches — never a
//              silent claim (supersedes F-041's name-click dual semantics).
//   - ready  → unassigned work-items (plain count)
//   - busy   → assigned work-items; clicking it reveals the inline roster (C3)
//   - tot    → readyWi + busyWi
//   - batches/docs → lane batch + document totals; clicking navigates (C3)
//   - lock   → ◯ open when free, ✕ when a supervisor has parked the lane
//
// The blocked variant (R-162 §10.1) dims the row, shows the reason as a hover
// tooltip, and disables the claim button. Empty lanes (tot 0) still render
// the full column set including the lock cell, so a parked-but-empty lane is
// visible (R-218).

interface JobTypeRowProps {
  jobTypeId: string;
  jobTypeName: string;
  /** Back-compat alias of `docs`. */
  count: number;
  readyWi: number;
  busyWi: number;
  totWi: number;
  batches: number;
  docs: number;
  isActive: boolean;
  onStart: (jobTypeId: string) => void;
  disabled?: boolean;
  /** R-162 §10.1 — this (step, jobType) lane is parked by a supervisor. */
  blocked?: boolean;
  /** Free-form supervisor reason (≤200 chars). Shown as the hover tooltip. */
  blockedReason?: string;
  /**
   * DE-1545 F-046 — count of failed/retrying export docs on this lane. When > 0
   * a loud "N failed" badge renders so a stuck export is impossible to miss.
   */
  exportFailed?: number;
  /**
   * E-057 follow-on — makes the failed badge a click-through to Export
   * History filtered to this job type's failures. Without it the badge stays
   * a plain (tooltip-only) span.
   */
  onShowExportFailures?: (jobTypeId: string) => void;
  /** C3 — busy-cell click reveals the inline roster for this lane. */
  onToggleRoster?: (jobTypeId: string) => void;
  /** C3 — job-name / batches / docs click navigates to filtered Batches. */
  onNavigate?: (jobTypeId: string) => void;
  /** C4 — operators see a read-only lock; supervisors can toggle it. */
  onToggleLock?: (jobTypeId: string) => void;
  /** C4 — false hides interactive lock affordance (read-only status only). */
  canToggleLock?: boolean;
}

export function JobTypeRow({
  jobTypeId,
  jobTypeName,
  readyWi,
  busyWi,
  totWi,
  batches,
  docs,
  isActive,
  onStart,
  disabled,
  blocked,
  blockedReason,
  exportFailed,
  onShowExportFailures,
  onToggleRoster,
  onNavigate,
  onToggleLock,
  canToggleLock,
}: JobTypeRowProps) {
  const className = [
    "qc-jt-row",
    isActive ? "qc-jt-row--active" : "",
    blocked ? "qc-jt-row--blocked" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const canStart = readyWi > 0 && !blocked && !disabled;

  // E-057 — the server counts DOCUMENTS in an export-error state across ALL
  // batches of this job type (unbounded history), not this row's batches; say
  // both honestly ("3 failed" on a one-batch row read as wrong counts and
  // drove a live misreading during the F-030 verify).
  const failedBadgeTitle = `${exportFailed} document${exportFailed === 1 ? "" : "s"} across all batches of this job type ${exportFailed === 1 ? "is" : "are"} in an export error state (includes older batches not shown here) — see Export History`;
  const failedBadgeText = `${exportFailed} doc${exportFailed === 1 ? "" : "s"} failed`;

  return (
    <div
      className={className}
      title={blocked ? blockedReason : undefined}
      aria-label={
        blocked
          ? `${jobTypeName} blocked — ${blockedReason ?? "queue on hold"}`
          : jobTypeName
      }
    >
      <span className="qc-jt-name-cell">
        {/* E-051 — the name is honest navigation to the lane's batches; it
            never claims. Claiming is the explicit button in the next cell. */}
        <button
          type="button"
          className="qc-jt-name qc-jt-cell-btn"
          onClick={() => onNavigate?.(jobTypeId)}
          disabled={!onNavigate}
          title={`Open ${jobTypeName} batches`}
          aria-label={`Open ${jobTypeName} batches`}
        >
          {jobTypeName}
        </button>
        {/* E-057 follow-on — when a handler is wired, the badge IS the
            click-through to Export History filtered to this job type's
            failures (the row context is job-type-scoped, so the per-batch
            rollup lives on Batch Detail instead). */}
        {exportFailed !== undefined && exportFailed > 0 &&
          (onShowExportFailures ? (
            <button
              type="button"
              className="qc-jt-failed qc-jt-failed--link"
              data-testid="jt-export-failed"
              title={failedBadgeTitle}
              aria-label={`Show ${jobTypeName} export failures in Export History`}
              onClick={() => onShowExportFailures(jobTypeId)}
            >
              {failedBadgeText}
            </button>
          ) : (
            <span
              className="qc-jt-failed"
              data-testid="jt-export-failed"
              title={failedBadgeTitle}
            >
              {failedBadgeText}
            </span>
          ))}
      </span>

      {/* E-051 — uniform explicit claim affordance: every feedable lane gets
          the same honestly-labeled control. Non-feedable lanes (export,
          terminal) render an empty cell so the grid stays aligned. */}
      <span className="qc-jt-claim-cell">
        {!disabled && (
          <button
            type="button"
            className="qc-jt-claim"
            data-testid="jt-claim"
            onClick={() => onStart(jobTypeId)}
            disabled={!canStart}
            title={
              blocked
                ? blockedReason ?? "Lane is blocked"
                : canStart
                  ? `Claim the next ready ${jobTypeName} document`
                  : "Nothing ready to claim"
            }
            aria-label={`Claim next: ${jobTypeName}`}
          >
            Claim next
          </button>
        )}
      </span>

      {/* E-051 — plain count; the claim path is the explicit button above. */}
      <span className="qc-jt-num qc-jt-ready" data-testid="jt-ready">
        {readyWi}
      </span>

      <button
        type="button"
        className="qc-jt-num qc-jt-busy qc-jt-cell-btn"
        data-testid="jt-busy"
        onClick={() => onToggleRoster?.(jobTypeId)}
        disabled={!onToggleRoster}
        title="Show operators on this lane"
      >
        {busyWi}
      </button>

      <span className="qc-jt-num qc-jt-tot" data-testid="jt-tot">
        {totWi}
      </span>

      <button
        type="button"
        className="qc-jt-num qc-jt-batches qc-jt-cell-btn"
        data-testid="jt-batches"
        onClick={() => onNavigate?.(jobTypeId)}
        disabled={!onNavigate}
        title="View batches"
      >
        {batches}
      </button>

      <button
        type="button"
        className="qc-jt-num qc-jt-docs qc-jt-cell-btn"
        data-testid="jt-docs"
        onClick={() => onNavigate?.(jobTypeId)}
        disabled={!onNavigate}
        title="View documents"
      >
        {docs}
      </button>

      <LockCell
        jobTypeId={jobTypeId}
        blocked={Boolean(blocked)}
        blockedReason={blockedReason}
        canToggle={Boolean(canToggleLock)}
        onToggle={onToggleLock}
      />
    </div>
  );
}

function LockCell({
  jobTypeId,
  blocked,
  blockedReason,
  canToggle,
  onToggle,
}: {
  jobTypeId: string;
  blocked: boolean;
  blockedReason?: string;
  canToggle: boolean;
  onToggle?: (jobTypeId: string) => void;
}) {
  const glyph = blocked ? "✕" : "◯";
  const label = blocked
    ? `Locked${blockedReason ? ` — ${blockedReason}` : ""}`
    : "Open";

  if (canToggle && onToggle) {
    return (
      <button
        type="button"
        className={`qc-jt-lock qc-jt-cell-btn${blocked ? " qc-jt-lock--closed" : ""}`}
        data-testid="jt-lock"
        onClick={() => onToggle(jobTypeId)}
        title={blocked ? `Unblock lane (${label})` : "Block lane"}
        aria-label={blocked ? "Unblock lane" : "Block lane"}
      >
        {glyph}
      </button>
    );
  }

  return (
    <span
      className={`qc-jt-lock${blocked ? " qc-jt-lock--closed" : ""}`}
      data-testid="jt-lock"
      title={label}
      aria-label={label}
    >
      {glyph}
    </span>
  );
}
